<script lang="ts">
  import { STORE } from "../../state.svelte";
  import { shortAddress } from "../../utils";
  import PoolBadge from "../elements/PoolBadge.svelte";
  import { onMount } from "svelte";

  let trades = $derived(STORE.bot.trades);

  // All aggregates are interval-based to avoid per-trade recomputation
  let totalBribes = $state(0);
  let tradesWithBribes = $state(0);
  let totalTradeCount = $state(0);
  let bribePct = $state(0);
  let avgBribe = $state(0);
  let largestTrade = $state<{ amount: number; pool: string; tx: string } | null>(null);
  let avgTradeSize = $state(0);
  let totalTrades = $state(0);
  let activePools = $state(0);
  let uniqueWallets = $state(0);
  let tradesPerSec = $state(0);
  let dexDistribution = $state<[string, number][]>([]);
  let dexTotal = $state(0);

  let tradeTimestamps: number[] = [];
  let prevTradeCount = 0;

  onMount(() => {
    const iv = setInterval(() => {
      // Snapshot all aggregates every 2 seconds
      totalBribes = STORE.bot.totalBribes;
      tradesWithBribes = STORE.bot.tradesWithBribes;
      totalTradeCount = STORE.bot.totalTradeCount;
      bribePct = totalTradeCount > 0 ? Math.round((tradesWithBribes / totalTradeCount) * 100) : 0;
      avgBribe = tradesWithBribes > 0 ? totalBribes / tradesWithBribes : 0;
      largestTrade = STORE.bot.largestTrade;

      // Avg trade size from last 50 trades
      const t = STORE.bot.trades;
      if (t.length > 0) {
        let sum = 0;
        for (const tr of t) sum += Math.abs(parseFloat(tr.quote_amount) || 0);
        avgTradeSize = sum / t.length;
      }

      // Pool stats aggregates
      const pools = Object.values(STORE.bot.poolsStats);
      let tt = 0;
      const walletSet = new Set<string>();
      const dist: Record<string, number> = {};
      for (const p of pools) {
        tt += p.tradesRawCount;
        dist[p.pool_type] = (dist[p.pool_type] || 0) + p.tradesRawCount;
        for (const w of Object.keys(p.wallets)) walletSet.add(w);
      }
      totalTrades = tt;
      activePools = pools.length;
      uniqueWallets = walletSet.size;
      dexDistribution = Object.entries(dist).sort((a, b) => b[1] - a[1]);
      dexTotal = tt;

      // Trades/sec
      if (tt > prevTradeCount) {
        const now = Date.now();
        for (let i = 0; i < tt - prevTradeCount; i++) tradeTimestamps.push(now);
        prevTradeCount = tt;
      }
      const cutoff = Date.now() - 10000;
      tradeTimestamps = tradeTimestamps.filter(ts => ts > cutoff);
      tradesPerSec = Math.round((tradeTimestamps.length / 10) * 10) / 10;
    }, 2000);
    return () => clearInterval(iv);
  });

  const formatSol = (val: number) => {
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    if (val >= 1) return val.toFixed(2);
    if (val >= 0.001) return val.toFixed(4);
    return val.toFixed(6);
  };

  const dexColors: Record<string, string> = {
    PumpSwapAmm: "bg-[#ff6b9d]",
    PumpFunAmm: "bg-[#00d18c]",
    RaydiumAmm: "bg-[#6c5ce7]",
    RaydiumCpmm: "bg-[#8b7cf7]",
    RaydiumClmm: "bg-[#a39af7]",
    RaydiumLaunchpad: "bg-[#5b4cd6]",
    MeteoraDyn: "bg-[#f5a623]",
    MeteoraDynV2: "bg-[#f7b84e]",
    MeteoraDlmm: "bg-[#e8961c]",
  };

  const getDexColor = (dex: string) => dexColors[dex] || "bg-gray-500";

  const getPoolInfo = (poolAddress: string) => {
    return STORE.bot.sailfish.getPoolInfo(poolAddress);
  };
</script>

<div class="px-4">
  <!-- Widgets Row -->
  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Total Trades</p>
      <p class="text-2xl font-black text-white tabular-nums">{totalTrades.toLocaleString()}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Active Pools</p>
      <p class="text-2xl font-black text-white tabular-nums">{activePools}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Wallets</p>
      <p class="text-2xl font-black text-white tabular-nums">{uniqueWallets}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Trades/sec</p>
      <p class="text-2xl font-black text-white tabular-nums">{tradesPerSec}<span class="text-sky-500 text-sm">/s</span></p>
    </div>
  </div>

  <!-- MEV & Alpha Insights -->
  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
    <div class="bg-gray-950/40 border border-sky-900/30 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-sky-500/60 uppercase tracking-widest mb-1">Total Bribes</p>
      <p class="text-2xl font-black text-sky-500 tabular-nums">{formatSol(totalBribes)}</p>
      <p class="text-[9px] text-gray-600 font-mono mt-1">SOL paid to validators</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Avg Bribe</p>
      <p class="text-2xl font-black text-white tabular-nums">{formatSol(avgBribe)}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Bribed Trades</p>
      <p class="text-2xl font-black text-white tabular-nums">{bribePct}<span class="text-sky-500 text-sm">%</span></p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Largest Trade</p>
      <p class="text-2xl font-black text-white tabular-nums">{largestTrade ? formatSol(largestTrade.amount) : "—"}</p>
    </div>
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-4 text-center">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Avg Trade Size</p>
      <p class="text-2xl font-black text-white tabular-nums">{formatSol(avgTradeSize)}</p>
    </div>
  </div>

  <!-- DEX Volume Distribution -->
  {#if dexDistribution.length > 0}
    <div class="mb-6">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-2">Volume by DEX</p>
      <div class="h-2 bg-gray-800 rounded-full flex overflow-hidden mb-2">
        {#each dexDistribution as [dex, count]}
          <div class="{getDexColor(dex)} transition-all duration-500" style="width: {(count / dexTotal) * 100}%"></div>
        {/each}
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono text-gray-500">
        {#each dexDistribution as [dex, count]}
          <span class="flex items-center gap-1">
            <span class="w-2 h-2 {getDexColor(dex)} rounded-full inline-block"></span>
            {dex} <span class="text-gray-700">{count}</span>
          </span>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Trade Table -->
  <div class="overflow-x-auto">
    <table class="w-full text-xs font-mono">
      <thead>
        <tr class="text-gray-600 text-left border-b border-gray-800">
          <th class="py-1.5 px-2 font-normal hidden md:table-cell">Slot</th>
          <th class="py-1.5 px-2 font-normal">DEX</th>
          <th class="py-1.5 px-2 font-normal">Pair</th>
          <th class="py-1.5 px-2 font-normal text-right">Quote</th>
          <th class="py-1.5 px-2 font-normal text-right hidden sm:table-cell">Base</th>
          <th class="py-1.5 px-2 font-normal text-right">Price</th>
          <th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Wallet</th>
          <th class="py-1.5 px-2 font-normal text-right">Tx</th>
        </tr>
      </thead>
      <tbody>
        {#each trades as trade}
          {@const poolInfo = getPoolInfo(trade.pool_address)}
          <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
            <td class="py-1.5 px-2 text-gray-500 tabular-nums hidden md:table-cell">{trade.index.tick}</td>
            <td class="py-1.5 px-2">{#if poolInfo}<PoolBadge poolType={poolInfo.pool_type} />{/if}</td>
            <td class="py-1.5 px-2">
              <a href={`https://dexscreener.com/solana/${trade.pool_address}`} target="_blank" class="text-white hover:text-sky-500 transition-colors">
                {#if poolInfo}{poolInfo.base_token.symbol}/{poolInfo.quote_token.symbol}{:else}{shortAddress(trade.pool_address, 4, 4)}{/if}
              </a>
            </td>
            <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums">{parseFloat(trade.quote_amount).toFixed(4)}</td>
            <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden sm:table-cell">{parseFloat(trade.base_amount).toFixed(4)}</td>
            <td class="py-1.5 px-2 text-right text-white tabular-nums">{parseFloat(trade.price).toFixed(6)}</td>
            <td class="py-1.5 px-2 text-right hidden md:table-cell">
              <a href={`https://solscan.io/account/${trade.from_wallet}`} target="_blank" class="text-gray-500 hover:text-white transition-colors">{shortAddress(trade.from_wallet, 4, 4)}</a>
            </td>
            <td class="py-1.5 px-2 text-right">
              <a href={`https://solscan.io/tx/${trade.index.tx_hash}`} target="_blank" class="text-sky-500/70 hover:text-sky-500 transition-colors">View</a>
            </td>
          </tr>
        {/each}
        {#if trades.length === 0}
          <tr><td colspan="8" class="py-8 text-center text-gray-600 italic">Waiting for trades...</td></tr>
        {/if}
      </tbody>
    </table>
  </div>
</div>
